import { Calendar } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";

/**
 * Handles the behavior of the events list
 */
let EventsList = {
	// keep the calendar object around
	calendar: null,
	/**
	 * Entry point
	 */
	_init: () => {
		// listen for document ready
		document.addEventListener('DOMContentLoaded', EventsList._ready);
	},
	/**
	 * Fires when document ready
	 */
	_ready: () => {
		// start pagination event listeners
		EventsList._addPaginationLinkListeners();

		// listen for custom events filter click
		document.addEventListener('eventsfilter:click', EventsList._eventsfilterClickHandler);

		// bind event listeners for calendar ui
		EventsList._listenForCalendarEvents();
	},
	/**
	 * Binds event listeners for the calendar UI & re-renders the calendar. Useful for UI refreshing after certain interactions.
	 */
	_listenForCalendarEvents: () => {
		// config/render the calendar
		const calendarEl = document.querySelector('.eventslist-calendarui-calendar');
		if (calendarEl instanceof HTMLElement) {
			EventsList.calendar = new Calendar(calendarEl, {
				plugins: [
					dayGridPlugin
				],
				// views: {
				// 	dayGridMonth: {
				// 		dayHeaderFormat: {
				// 			weekday: 'long'
				// 		}
				// 	}
				// },
				initialView: 'dayGridMonth',
				weekends: true,
				eventInteractive: true,
				events: (function () {
					// put in all events but render the "there's an event today" icon only once per day
					const events_data = [];
					if (typeof caldata != 'undefined') {
						caldata.all_events.forEach((all_event) => {
							// Normalize the date to midnight
							const eventStartDate = new Date(`${all_event.StartDate}T${all_event.StartTime}`).setHours(0, 0, 0, 0);

							// Check if the event date already exists in the events_data
							const isDuplicate = events_data.some(event_data => {
								// Check if any existing event has the same start date (normalized to midnight)
								return new Date(event_data.start).setHours(0, 0, 0, 0) === eventStartDate;
							});

							// Only add the event if it's not a duplicate
							if (!isDuplicate) {
								events_data.push({
									title: all_event.post_title,
									start: new Date(`${all_event.StartDate}T${all_event.StartTime}`)
								});
							}
						});
					}
					return events_data;
				})(),
				headerToolbar: false,
				fixedWeekCount: false,
				contentHeight: 'auto',

				// Hook that runs after each day cell is mounted
				dayCellClassNames: function (info) {
					// Normalize info.date to midnight UTC
					const dayDate = new Date(info.date.setHours(0, 0, 0, 0)).toISOString().split('T')[0];

					// Check if the day has any events
					const eventsForDay = EventsList.calendar.getEvents().filter(event => {
						// Normalize event's start date to midnight UTC
						const eventStartDate = new Date(event.start).setHours(0, 0, 0, 0);
						return new Date(eventStartDate).toISOString().split('T')[0] === dayDate;
					});

					// If events are found, add a custom class
					if (eventsForDay.length > 0) {
						return ['has-events'];
					}

					// If no events, return an empty array (no class)
					return [];
				},
				eventContent: { html: `<svg width="6" height="6" viewBox="0 0 6 6" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="3" cy="3" r="3" fill="#01A847"/></svg>` }
			});

			// re-render the calendar
			EventsList.calendar.render();
		}

		// listen for calendar toggle click
		const calendarToggle = document.querySelector('.eventslist-wrapper-selectdate-link');
		if (calendarToggle instanceof HTMLElement) {
			calendarToggle.addEventListener('click', EventsList._calendarToggleClickHandler);
		}

		// listen for calendar month next/prev click
		const monthPrev = document.querySelector('.eventslist-calendarui-monthyear-prev');
		const monthNext = document.querySelector('.eventslist-calendarui-monthyear-next');

		if (monthPrev instanceof HTMLElement && monthNext instanceof HTMLElement && EventsList.calendar !== null) {
			monthPrev.addEventListener('click', EventsList._monthPrevClickHandler)
			monthNext.addEventListener('click', EventsList._monthNextClickHandler)
		}

		// listen for calendar close click
		const calendarClose = document.querySelector('.eventslist-calendarui-close');
		if (calendarClose instanceof HTMLElement) {
			calendarClose.addEventListener('click', EventsList._calendarCloseClickHandler);
		}

		// listen for the monthyear picker click
		const pickerToggle = document.querySelector('.eventslist-calendarui-monthyear-toggle');

		if (pickerToggle instanceof HTMLElement) {
			pickerToggle.addEventListener('click', EventsList._pickerToggleClickHandler);
		}

		// listen for the monthyear picker month/year links click
		const monthyearLinks = document.querySelectorAll('.eventslist-calendarui-monthyear-picker-wrapper-list-years-year-link, .eventslist-calendarui-monthyear-picker-wrapper-list-months-month-link');
		if (monthyearLinks.length > 0) {
			monthyearLinks.forEach((monthyearLink) => {
				monthyearLink.addEventListener('click', EventsList._monthyearLinkClickHandler);
			});
		}

		// listen for the monthyear picker close click
		const monthyearCloses = document.querySelectorAll('.eventslist-calendarui-monthyear-picker-wrapper-close, .eventslist-calendarui-monthyear-picker-wrapper-seeevents');
		if (monthyearCloses.length > 0) {
			monthyearCloses.forEach((monthyearClose) => {
				monthyearClose.addEventListener('click', EventsList._monthyearCloseClickHandler);
			});
		}

		const calendarButtons = document.querySelectorAll('.eventslist-calendarui-buttons-button');

		if (calendarButtons.length > 0) {
			calendarButtons.forEach((button) => {
				button.addEventListener('click', EventsList._calendarButtonClickHandler);
			});
		}
	},
	_calendarButtonClickHandler: (e) => {
		// dont put # in url.
		e.preventDefault();

		// check for clear button click
		if (e.target.classList.contains('clear')) {
			// remove the dateon search query param
			const url = new URL(window.location);
			url.searchParams.delete("dateon");
			url.searchParams.delete("event_cat")
			history.pushState({}, "", url);

			// re-query events and the events filter
			e.target.dispatchEvent( new CustomEvent('calendarClear:click',
					{
						bubbles: true,
					}
				)
			);
			EventsList._eventsfilterClickHandler();

			document.querySelector('header.wp-block-template-part').classList.remove('low-zindex');
			document.querySelector('footer.wp-block-template-part').classList.remove('low-zindex');
			// Allow scrolling again
			document.querySelector('body').classList.remove('no-scroll');
			window.scrollTo(0,0);
		}

		// refresh date titles
		EventsList._updateCalendarTitles();

		// refresh monthyear active link
		EventsList._updateActiveMonthyearLinks();

		// re-bind day click listeners
		EventsList._listenForCalendarDayClicks();
		
	},
	_listenForCalendarDayClicks: () => {
		const calendarDaysWithEvents = document.querySelectorAll('td.fc-day.has-events');
		if (calendarDaysWithEvents.length > 0) {
			calendarDaysWithEvents.forEach((day) => {
				day.addEventListener('click', EventsList._calendarDayClickHandler);
			});
		}
	},
	_calendarDayClickHandler: (e) => {
		// get the date like YYYY-MM-DD
		const date = e.target.dataset.date;

		// update the URL & history
		const url = new URL(window.location);

		url.searchParams.set("dateon", encodeURIComponent(date));
		url.searchParams.delete("page_num");
		url.searchParams.delete("event_cat");

		history.pushState({}, "", url);

		// re-query events
		e.target.dispatchEvent( new CustomEvent('calendarDay:click',
				{
					bubbles: true,
				}
			)
		);
		EventsList._eventsfilterClickHandler();

		document.querySelector('header.wp-block-template-part').classList.remove('low-zindex');
		document.querySelector('footer.wp-block-template-part').classList.remove('low-zindex');
		window.scrollTo(0,0);

		// Allow scrolling again
		document.querySelector('body').classList.remove('no-scroll');
	},
	/**
	 * Handles the calendar ui opening.
	 * @param {object} e the event object
	 */
	_calendarToggleClickHandler: (e) => {
		// prevent # from being in url
		e.preventDefault();

		// show the calendar ui
		document.querySelector('.eventslist-calendarui').classList.add('active');

		window.scrollTo(0,0);
		// re-render the calendar
		EventsList.calendar.render();

		// re-bind day click listeners
		EventsList._listenForCalendarDayClicks();

		// hide the nav
		const mainNav = document.querySelector('header.wp-block-template-part')
		mainNav.classList.add('low-zindex');
		document.querySelector('footer.wp-block-template-part').classList.add('low-zindex');
		if (mainNav.classList.contains('hidden')) { 
			document.querySelector('header.wp-block-template-part').classList.remove('hidden');
		}
		// hide the month/year picker

		// Don't allow scrolling
		document.querySelector('body').classList.add('no-scroll');
	},
	/**
	 * Handle picker toggle click. Expands month/year picker UI
	 * @param {object} e the event obj
	 */
	_pickerToggleClickHandler: (e) => {
		// no # in url
		e.preventDefault();

		// show the picker
		document.querySelector('.eventslist-calendarui-monthyear-picker').classList.add('active');
	},
	/**
	 * Handles the monthyear picker link clicks and updates the calendadr.
	 * @param {object} e the event object
	 */
	_monthyearLinkClickHandler: (e) => {
		// prevent # showing up in the url
		e.preventDefault();
		// remove all active classes from neighboring links
		e.target.closest('ul').querySelectorAll('a').forEach(el => el.classList.remove('active'));
		// set the clicked link to "active"
		e.target.classList.add('active');

		const month = document.querySelector('.eventslist-calendarui-monthyear-picker-wrapper-list-months-month-link.active').dataset.month;
		const year = document.querySelector('.eventslist-calendarui-monthyear-picker-wrapper-list-years-year-link.active').dataset.year;
		// month - 1 because JavaScript months are 0-indexed
		const date = new Date(year, month - 1, 1);

		// update the calendar date
		EventsList.calendar.gotoDate(date);

		// re-bind day click listeners
		EventsList._listenForCalendarDayClicks();

		// refresh date titles
		EventsList._updateCalendarTitles();
	},
	/**
	 * Update calendar titles UI
	 */
	_updateCalendarTitles: () => {
		document.querySelector('.eventslist-calendarui-monthyear-text').innerHTML = EventsList.calendar.view.title;
		document.querySelector('.eventslist-calendarui-monthyear-picker-wrapper-text').innerHTML = EventsList.calendar.view.title;
	},
	/**
	 * Update the monthyear active links
	 */
	_updateActiveMonthyearLinks: () => {
		// update the monthyear picker active year/month
		// this is a Date object representing the start of the current view
		const currentDate = EventsList.calendar.view.currentStart;

		const year = currentDate.getFullYear();
		// getMonth() is zero-based (0 = January, 1 = February, etc.)
		const month = currentDate.getMonth() + 1;

		// strip the active classes from the links
		document.querySelectorAll('.eventslist-calendarui-monthyear-picker-wrapper-list-months-month-link').forEach((link) => {
			link.classList.remove('active')
		});
		document.querySelectorAll('.eventslist-calendarui-monthyear-picker-wrapper-list-years-year-link').forEach((link) => {
			link.classList.remove('active')
		});

		// set the correct links with based on current month year
		document.querySelector(`.eventslist-calendarui-monthyear-picker-wrapper-list-months-month-link[data-month="${month}"]`).classList.add('active');
		document.querySelector(`.eventslist-calendarui-monthyear-picker-wrapper-list-years-year-link[data-year="${year}"]`).classList.add('active');
	},
	/**
	 * Hides the month/year picker on click
	 * @param {object} e the event object
	 */
	_monthyearCloseClickHandler: (e) => {
		// prevent # from showing up in the url
		e.preventDefault();

		// hide the picker
		document.querySelector('.eventslist-calendarui-monthyear-picker').classList.remove('active');
	},
	/**
	 * Handles the month previous click. Set the calendar view back a month & updates the calendar month/year title.
	 * @param {object} e the event object
	 */
	_monthPrevClickHandler: (e) => {
		// prevent # in url
		e.preventDefault();

		// move the calendar back a month
		EventsList.calendar.prev();

		// refresh date titles
		EventsList._updateCalendarTitles();

		// refresh monthyear active link
		EventsList._updateActiveMonthyearLinks();

		// re-bind day click listeners
		EventsList._listenForCalendarDayClicks();
	},
	/**
	 * Handles the month next click. Set the calendar view forward a month & updates the calendar month/year title.
	 * @param {object} e the event object
	 */
	_monthNextClickHandler: (e) => {
		// prevent # in url
		e.preventDefault();

		// move the calendar forward a month
		EventsList.calendar.next();

		// refresh date titles
		EventsList._updateCalendarTitles();

		// refresh monthyear active link
		EventsList._updateActiveMonthyearLinks();

		// re-bind day click listeners
		EventsList._listenForCalendarDayClicks();
	},
	_calendarCloseClickHandler: (e) => {
		// prevent # in url
		e.preventDefault();

		// make the header and footer normal again
		document.querySelector('header.wp-block-template-part').classList.remove('low-zindex');
		document.querySelector('footer.wp-block-template-part').classList.remove('low-zindex');

		// Allow scrolling again
		document.querySelector('body').classList.remove('no-scroll');

		// hide the calendar ui
		document.querySelector('.eventslist-calendarui').classList.remove('active');
	},
	/**
	 * Listen for pagination link clicks
	 */
	_addPaginationLinkListeners: () => {
		// get pagination links
		const paginationLinks = document.querySelectorAll('.eventslist-wrapper-pagination a');

		// do they exist?
		if (paginationLinks.length > 0) {
			// loop & listen for click
			paginationLinks.forEach((paginationLink) => {
				paginationLink.addEventListener('click', EventsList._paginationLinkClickHandler);
			});
		}
	},
	/**
	 * Fired when clicking a pagination link
	 * @param {object} e the event object
	 */
	_paginationLinkClickHandler: (e) => {
		// prevent # from showing up in the URL
		e.preventDefault();

		// update the URL & history
		const url = new URL(window.location);

		url.searchParams.set("page_num", parseInt(e.target.innerHTML));

		history.pushState({}, "", url);

		// rebuild list
		EventsList._eventsfilterClickHandler();
	},
	/**
	 * Fired when the events filter has been clicked
	 */
	_eventsfilterClickHandler: async () => {
		// refresh the events list via the api
		try {
			// map all current search params to the fetch url
			let currenturl = new URL(window.location);
			let fetchurl = new URL(ajaxurl);
			currenturl.searchParams.forEach((value, key) => {
				fetchurl.searchParams.append(key, value)
			}); 

			// build the form data to send to the BE
			let formData = new FormData();
			formData.append('action', 'refresh_events_list');

			// use async/await fetch to get the response
			const res = await fetch(fetchurl, {
				method: 'POST',
				body: formData,
			});
			const eventsList = await res.text();

			// render the response
			document.querySelector('.eventslist').outerHTML = eventsList;

			// rebind pagination link listeners
			EventsList._addPaginationLinkListeners();

			// rebind calendar ui listeners
			EventsList._listenForCalendarEvents();
			
			document.dispatchEvent(new Event('list:updated'));
		} catch (error) {
			console.log(error);
		}
	}
};

// beginning.
EventsList._init();
